/**
 * Deep-link `?farm=` → область сессии (AK-897, AK-866 В-3).
 *
 * С AK-866 хозяйство у всех экранов одно — то, что выбрано в переключателе
 * «Хозяйство ▾» (`FarmSwitcher`). Своих выборов хозяйства на экранах больше
 * нет. Но присланная ссылка («посмотри вариант Зимовников») обязана открыть
 * ИМЕННО то хозяйство, о котором её прислали, — иначе ссылка врёт.
 *
 * Поэтому ссылка не перекрывает область локально (это и был «второй контрол»,
 * который снимало В-3), а ПЕРЕКЛЮЧАЕТ её — тем же единственным путём записи,
 * что и сам переключатель (`useScopeSetter`, один `POST /scope/set`). После
 * переключения `?farm=` из адреса убирается: дальше источник один — сессия.
 *
 * Состояния:
 *   - `none`      — ссылки нет, или она уже совпадает с областью сессии;
 *   - `waiting`   — список доступного ещё не пришёл: судить о доступе рано;
 *   - `switching` — хозяйство доступно, область переключается; экран уже
 *                   может показывать данные этого хозяйства;
 *   - `no-access` — хозяйства нет среди доступных: «Нет доступа к хозяйству».
 *                   Область НЕ трогаем, `?farm=` оставляем — человек должен
 *                   видеть, почему ссылка не открылась;
 *   - `error`     — сервер отказал в переключении; можно повторить.
 *
 * Обычный рендер и смена фильтров экрана `/scope/set` не зовут — только
 * ссылка, чьё хозяйство расходится с областью сессии, и только один раз на
 * ссылку (повтор — явной кнопкой).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScopeChangedDetail } from '@/lib/scopeEvent';
import { rememberLastScope } from './lastScope';
import { useAvailableScopes } from './useAvailableScopes';
import { useScopeSetter } from './useScopeSetter';

export type FarmDeepLink =
  | { state: 'none' }
  | { state: 'waiting'; farmId: string }
  | { state: 'switching'; farmId: string }
  | { state: 'no-access'; farmId: string }
  | { state: 'error'; farmId: string; message: string; retry: () => void };

export interface FarmDeepLinkResult {
  link: FarmDeepLink;
  /**
   * Событие смены области — от нашего же переключения по ссылке? Экран
   * сбрасывает свои фильтры (вариант, строка) на смену хозяйства в
   * переключателе, но НЕ на применение ссылки: `?farm=X&variant=V` обязан
   * открыть вариант V, а не потерять его вместе с `?farm=`.
   */
  isOwnSwitch: (detail: ScopeChangedDetail) => boolean;
}

export function useFarmDeepLink(
  linkFarm: string | null | undefined,
  /** Убрать `?farm=` из адреса — экран знает форму своего search сам. */
  onApplied: () => void,
): FarmDeepLinkResult {
  const scopes = useAvailableScopes();
  const { setScope, error, clearError } = useScopeSetter('farm-deep-link');
  const [attempt, setAttempt] = useState(0);
  const triedRef = useRef<string | null>(null);
  const switchingTo = useRef<string | null>(null);
  // Для какой ссылки адрес уже почищен — чтобы не звать `onApplied` дважды
  // (после ответа `/scope/set` и ещё раз, когда кэш догонит область).
  const appliedFor = useRef<string | null>(null);
  const appliedRef = useRef(onApplied);
  appliedRef.current = onApplied;

  const link = linkFarm || null;
  const data = scopes.data;
  const isCurrent =
    !!link && data?.current?.scope_type === 'farm' && data.current.scope_id === link;
  const known =
    !!link && !!data && data.available.some((s) => s.scope_type === 'farm' && s.scope_id === link);

  useEffect(() => {
    // Ссылка ушла из адреса — следующая (даже на то же хозяйство) отработает
    // заново: человек мог переключиться в другое между ними.
    if (!link) {
      triedRef.current = null;
      appliedFor.current = null;
      return;
    }
    if (!data) return;
    // Ссылка совпала с областью (сразу или после переключения) — она своё
    // отработала, адрес чистим: второго источника хозяйства быть не должно.
    if (isCurrent) {
      if (appliedFor.current !== link) {
        appliedFor.current = link;
        appliedRef.current();
      }
      return;
    }
    if (!known) return;
    const key = `${link}#${attempt}`;
    if (triedRef.current === key) return;
    triedRef.current = key;
    switchingTo.current = link;
    void setScope({ scope_type: 'farm', scope_id: link })
      .then((ok) => {
        // Открыл по ссылке — значит, работает в этом хозяйстве: его и
        // откроем при следующем входе (AK-896).
        if (ok) rememberLastScope({ scope_type: 'farm', scope_id: link });
        // Сервер записал область — ссылка отработала, не дожидаясь, пока
        // фоновый перезапрос `/scope/available` догонит кэш.
        if (ok && appliedFor.current !== link) {
          appliedFor.current = link;
          appliedRef.current();
        }
      })
      .finally(() => {
        switchingTo.current = null;
      });
  }, [link, data, isCurrent, known, attempt, setScope]);

  const retry = useCallback(() => {
    clearError();
    setAttempt((a) => a + 1);
  }, [clearError]);

  const isOwnSwitch = useCallback(
    (detail: ScopeChangedDetail) =>
      detail.scope_type === 'farm' &&
      switchingTo.current !== null &&
      detail.scope_id === switchingTo.current,
    [],
  );

  let result: FarmDeepLink;
  if (!link || isCurrent) result = { state: 'none' };
  else if (!data) result = { state: 'waiting', farmId: link };
  else if (!known) result = { state: 'no-access', farmId: link };
  else if (error) result = { state: 'error', farmId: link, message: error, retry };
  else result = { state: 'switching', farmId: link };

  return { link: result, isOwnSwitch };
}
